import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { ulid } from 'ulid';
import { useLive, useRuntime } from './context';
import { useRemote } from './useRemote';
import { centsFromInput } from '../money/input';
import { formatClock, formatMoney } from '../mirror/time';
import { PaymentLinkCreated, PaymentLinkList } from '../wire';
import type { PaymentLink } from '../wire';
import { useLocale, useT } from '../messages';

/**
 * **"Text them a deposit link"** (spec §3.7), from the appointment.
 *
 * ## Two ways to price it, and the screen says which happened
 *
 * §6.5: *"Trust follows the actor, not the endpoint."* The partner surface has
 * no path to an amount it chose; the owner's own till does, because "the owner
 * may charge any amount on their own till, and the system's job is not to
 * prevent it but to record it". So the amount field is **empty by default** and
 * labelled as an override rather than as a price — leaving it alone means "our
 * deposit policy", which is what `depositRequiredCents` answers and the only
 * thing an untrusted caller could ever get. `priced_by` comes back on the
 * created link (`policy` or `staff`) and is shown, because §7.4 rule 3's
 * discipline is to record what happened rather than to reconstruct it.
 *
 * ## The URL is shown, not just copied
 *
 * A counter texts this link from a phone that is not the iPad. Copy-to-clipboard
 * is the fast path; the URL in full is the one that works when the clipboard is
 * unavailable, which on an installed PWA behind a permission prompt is often.
 *
 * ## No payments configured is a sentence, not a crash
 *
 * `core-api` answers `503` with the reason an owner can act on — a PayPal app
 * that has not been connected is a setup step, not a facet they were never
 * granted (which is what `501` would mean). The panel renders that sentence and
 * hides the button, because offering a control that cannot work is worse than
 * offering none.
 */

export function PaymentLinksPanel({
  appointmentId,
  itemName,
  onClose,
}: {
  appointmentId: string;
  itemName: string;
  onClose: () => void;
}): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const locale = useLocale();
  const session = useLive(() => runtime.mirror.session(), null);
  // The currency comes off each **link** rather than off the session: the link
  // carries the currency it was minted in, and a tenant that changed theirs
  // would otherwise have old links relabelled in a currency nobody was charged.
  const timezone = session?.tenant.timezone ?? 'UTC';

  const links = useRemote(
    (signal) =>
      runtime.api.request({
        method: 'GET',
        path: `/appointments/${appointmentId}/payment-links`,
        schema: PaymentLinkList,
        signal,
      }),
    [appointmentId],
    // A guest paying is `payment.*` on the stream; the poll is the backstop
    // while somebody is watching the panel waiting for it to turn green.
    { cue: (type) => type.startsWith('payment'), pollMs: 15_000 },
  );

  const [amountText, setAmountText] = useState('');
  const [creating, setCreating] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const rows = links.data?.payment_links ?? [];
  // The `503` sentence, whichever read or write produced it. It is the same
  // message from both routes and it is the owner's next step.
  const unavailable = failure !== null && /PayPal app|pay links/i.test(failure) ? failure : null;

  const create = useCallback(async (): Promise<void> => {
    setCreating(true);
    setFailure(null);
    try {
      // Blank is "the policy prices it". Anything typed that is not money is a
      // refusal here rather than a request the server has to reject — the owner
      // is standing at the counter and the round trip tells them nothing.
      const typed = amountText.trim() === '' ? null : centsFromInput(amountText);
      if (amountText.trim() !== '' && (typed === null || typed < 1)) {
        setFailure(t('paylink.notAnAmount'));
        return;
      }
      const amountCents = typed === null ? undefined : typed;
      await runtime.api.request({
        method: 'POST',
        path: `/appointments/${appointmentId}/payment-link`,
        body: {
          ...(amountCents === undefined ? {} : { amount_cents: amountCents }),
          // **Not** translated, deliberately. This string is a payload: it is
          // stored on the link and is what the *guest* reads in PayPal. The
          // staff device's language is the wrong language for it — a Spanish
          // till would put Spanish in front of an English-reading guest — and
          // the right one is the guest's, which only the server knows. It
          // stays English until `core-api` renders it per recipient locale
          // (docs/i18n-design.md §2, Domain C).
          description: `Deposit — ${itemName}`,
        },
        idempotencyKey: ulid(),
        schema: PaymentLinkCreated,
      });
      setAmountText('');
      links.reload();
    } catch (e) {
      // Including the `503`: `core-api` writes the sentence an owner can act
      // on, and it is shown exactly as it came.
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  }, [runtime, appointmentId, amountText, itemName, links]);

  const copy = useCallback((link: PaymentLink): void => {
    setCopied(link.payment_link_id);
    void navigator.clipboard?.writeText(link.url).catch(() => undefined);
  }, []);

  return (
    <div className="sheet" role="dialog" aria-label={t('paylink.dialogLabel')}>
      <div className="sheet__panel">
        <h2>{t('paylink.title')}</h2>
        {/* The service's own name, as the tenant wrote it in their menu. */}
        <p className="muted">{itemName}</p>

        {unavailable === null ? null : (
          <p className="error" role="alert">
            {unavailable}
          </p>
        )}

        <ul className="report-list payment-link-list">
          {rows.map((link) => (
            <li key={link.payment_link_id} className={`payment-link--${link.status}`}>
              <div>
                <strong>{formatMoney(link.amount_cents, link.currency, locale)}</strong>
                <small>
                  {/*
                    Both words, on purpose: `status` is the agreement's four-value
                    fold and `state` is the stored five. "approved" means they
                    clicked through and the money has not landed — the difference
                    between chasing somebody and waiting a moment.
                  */}
                  {link.status}
                  {link.state === 'approved' ? t('paylink.approvedNotCaptured') : ''}
                  {link.status === 'pending'
                    ? t('paylink.expires', {
                        clock: formatClock(link.expires_at, timezone, locale),
                      })
                    : ''}
                  {link.completed_at === null
                    ? ''
                    : t('paylink.paidAt', {
                        clock: formatClock(link.completed_at, timezone, locale),
                      })}
                </small>
                {link.last_error === null ? null : (
                  <small className="muted">{link.last_error}</small>
                )}
                <small className="payment-link__url">{link.url}</small>
              </div>
              <button type="button" className="link-button" onClick={() => copy(link)}>
                {copied === link.payment_link_id ? t('paylink.copied') : t('paylink.copy')}
              </button>
            </li>
          ))}
          {rows.length === 0 && !links.loading ? (
            <li className="muted">{t('paylink.none')}</li>
          ) : null}
        </ul>

        {unavailable !== null ? null : (
          <div className="editor editor--inline">
            <label className="field">
              <span>{t('paylink.amountLabel')}</span>
              <input
                type="text"
                inputMode="decimal"
                value={amountText}
                placeholder={t('paylink.amountPlaceholder')}
                onChange={(e) => setAmountText(e.target.value)}
              />
            </label>
            <small className="muted">{t('paylink.amountHint')}</small>
            <div className="sheet__actions">
              <button
                type="button"
                className="button button--primary"
                disabled={creating}
                onClick={() => void create()}
              >
                {creating ? t('paylink.creating') : t('paylink.create')}
              </button>
            </div>
          </div>
        )}

        {failure !== null && unavailable === null ? (
          <p className="error" role="alert">
            {failure}
          </p>
        ) : null}

        <div className="sheet__actions">
          <button type="button" className="button" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
